import {
  Box,
  Button,
  Container,
  FormControl,
  Heading,
  Text,
} from '@everlywell/ui-kit';
import StickyFooter from 'components/StickyFooter';
import { usePrePurchaseQuizContext } from 'contexts/PrePurchaseQuiz/PrePurchaseQuizContext';
import React, { useState } from 'react';

import Option, { OptionProps } from './Option';

// Types ----------

export interface OptionSelectFormProps {
  title: string;
  eyebrow?: string;
  tooltip?: string;
  options: OptionProps[];
  optionType: string;
  questionType: 'singleSelect' | 'multiSelect';
}

export interface UserSelectionProps {
  question: string;
  answer: string | string[];
}

// OptionSelectForm ------

const OptionSelectForm: React.FC<OptionSelectFormProps> = (props) => {
  const { nextStep, setQuestionsAndAnswers, setIsAnswerIneligible } =
    usePrePurchaseQuizContext();

  const { title, eyebrow, tooltip, options, questionType, optionType } = props;

  // Initialize user selections with question title and blank answers
  const blankUserSelections =
    questionType === 'multiSelect'
      ? {
          question: title,
          answer: [],
        }
      : {
          question: title,
          answer: '',
        };

  const [userSelections, setUserSelections] =
    useState<UserSelectionProps>(blankUserSelections);

  /**
   * Handle Next button click
   */
  const handleNext = () => {
    setQuestionsAndAnswers(userSelections);

    // Eligibility Evaluation - Single Select
    if (questionType === 'singleSelect') {
      const isSelectionIneligible = options.filter(
        (option) => option.title === userSelections.answer,
      )[0].isIneligible;

      setIsAnswerIneligible(isSelectionIneligible);
    }

    // Eligibility Evaluation - Multi Select
    if (Array.isArray(userSelections.answer)) {
      const isSelectionIneligible = userSelections.answer.some(
        (individualAnswer) =>
          options.some(
            (option) =>
              option.title === individualAnswer && option.isIneligible,
          ),
      );

      setIsAnswerIneligible(isSelectionIneligible);
    }

    nextStep();
  };

  /**
   * Get exclusive selection
   * @returns exclusive selection
   */
  const getExclusiveSelection = () => {
    if (Array.isArray(userSelections.answer)) {
      const exclusiveSelection = userSelections.answer.filter((answer) =>
        options.some((option) => option.title === answer && option.isExclusive),
      )[0];

      return exclusiveSelection;
    }
  };

  /**
   * Update single select answer. If the option is already selected, deselect it.
   * @param option
   **/
  const updateSingleSelect = (option: OptionProps) => {
    const { title } = option;
    const currentAnswer = userSelections.answer;
    const newAnswer = currentAnswer === title ? '' : title;

    setUserSelections({
      ...userSelections,
      answer: newAnswer,
    });
  };

  /**
   * Update multi select answer. If the option is already selected, deselect it.
   * If the option is exclusive, deselect all other options.
   * @param option
   */
  const updateMultiSelect = (option: OptionProps) => {
    const { title, isExclusive } = option;
    let currentAnswers = userSelections.answer;

    if (!Array.isArray(currentAnswers)) {
      return;
    }

    let newAnswers;
    if (isExclusive) {
      // Toggle exclusive selection
      newAnswers = currentAnswers.includes(title) ? [] : [title];
    } else {
      // Remove exclusive selection if it exists
      const exclusiveSelection = getExclusiveSelection();
      if (exclusiveSelection) {
        currentAnswers = currentAnswers.filter(
          (answer) => answer !== exclusiveSelection,
        );
      }

      // Toggle non-exclusive selection
      if (currentAnswers.includes(title)) {
        newAnswers = currentAnswers.filter((answer) => answer !== title);
      } else {
        newAnswers = [...currentAnswers, title];
      }
    }

    setUserSelections({
      ...userSelections,
      answer: newAnswers,
    });
  };

  /**
   * Handle Quiz Question option selection
   * @param questionType
   * @param index
   * @param value
   */
  const handleOptionSelection = (questionType: string, option: OptionProps) => {
    if (questionType === 'singleSelect') {
      updateSingleSelect(option);
    } else if (questionType === 'multiSelect') {
      updateMultiSelect(option);
    }
  };

  /**
   * Generate list of Option components
   */
  const optionsList = options.map((option, index) => {
    const isSelected = userSelections.answer.includes(option.title);

    return (
      <Option
        key={index}
        {...option}
        handleOptionSelection={() =>
          handleOptionSelection(questionType, option)
        }
        isSelected={isSelected}
      />
    );
  });

  return (
    <>
      <Box>
        <FormControl>
          <Box marginBottom="8">
            <Heading
              size="xl"
              color="eggplant.base"
              opacity="0.6"
              maxWidth="100%"
              display="block"
            >
              {eyebrow}
            </Heading>
            <Heading
              size="xl"
              color="tints.black"
              maxWidth="100%"
              display="block"
            >
              {title}
            </Heading>
          </Box>

          <Text size="md" color="tints.darkGrey" marginBottom="3">
            {tooltip}
          </Text>

          <Box display="flex" flexWrap="wrap" marginBottom="8">
            {optionType === 'titleAndDescription' &&
              optionsList.map((option, index) => (
                <Box
                  paddingRight={index % 2 === 0 ? [0, null, '6px'] : '0'}
                  paddingLeft={index % 2 !== 0 ? [0, null, '6px'] : '0'}
                  width={['100%', null, '50%']}
                  marginBottom="3"
                  key={`option-${index}`}
                >
                  {option}
                </Box>
              ))}

            {optionType === 'titleOnly' &&
              options.map((option, index) => {
                const isSelected = userSelections.answer.includes(option.title);

                return (
                  <Option
                    key={index}
                    {...option}
                    handleOptionSelection={() =>
                      handleOptionSelection(questionType, option)
                    }
                    isSelected={isSelected}
                  />
                );
              })}
          </Box>
        </FormControl>
      </Box>
      <StickyFooter type="fixed">
        <Container size={['sm', 'sm', 'fluid']} paddingY={['6', null, '0']}>
          <Button
            onClick={() => handleNext()}
            width={['100%', null, 'fit-content']}
            isDisabled={userSelections.answer.length === 0}
          >
            Next
          </Button>
        </Container>
      </StickyFooter>
    </>
  );
};

// Export ---------

export default React.memo(OptionSelectForm);
